import { ReactNode, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import StorageService from '../../services/storageService';
import { useGetUserPermission } from '../../hooks/api/user';
import { setUserPermission } from '../../store/slices/permission';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import _ from 'lodash';
import { setLogin } from '../../store/slices/auth';

type ModulePermissions = {
  [key: string]: boolean;
}

type RouteInternalProps = {
  children: ReactNode;
  requiredPermission?: keyof ModulePermissions;
  requiredDetailedPermission?: string;
};

const RouteInternal = ({ children, requiredPermission, requiredDetailedPermission }: RouteInternalProps) => {
  const dispatch = useDispatch();
  const userData = StorageService.getUserInfo();
  const storedModulePermissions = StorageService.getModulePermissions();
  const storedDetailedPermissions = StorageService.getDetailedPermissions();
  const selectedExtranet = StorageService.getSelectedExtranet();
  // Fetch rules:
  // - Channel Partner/Customers: fetch if not cached
  // - Barksdale: fetch ONLY after extranet is selected
  const isBarksdale = userData?.accessLevel === 'BARKSDALE';
  let shouldFetch = !storedModulePermissions || !storedDetailedPermissions;
  if (isBarksdale) {
    shouldFetch = (!!selectedExtranet) && shouldFetch;
  }
  
  // Ensure we always pass the selected extranet to maintain consistency
  const { data: permissionData } = useGetUserPermission(shouldFetch, selectedExtranet || undefined);

  const modulePermissions = useSelector((state: RootState) => state.permission.modulePermissions) as ModulePermissions;
  if (_.isEmpty(modulePermissions)) {
    dispatch(setUserPermission({
      modulePermissions: storedModulePermissions,
      detailedPermissions: storedDetailedPermissions
    }))
  }

  const userInfo = useSelector((state: RootState) => state.auth.user);
  if (_.isEmpty(userInfo)) {
    dispatch(setLogin({
      user: userData,
      token: StorageService.getAccessToken() ?? ''
    }))
  }

  useEffect(() => {
    if (permissionData?.modulePermissions && permissionData?.detailedPermissions) {
      // Always update permissions when new data comes in, especially when extranet selection changes
      dispatch(setUserPermission({
        modulePermissions: permissionData.modulePermissions,
        detailedPermissions: permissionData.detailedPermissions
      }))
      StorageService.setModulePermissions(permissionData.modulePermissions);
      StorageService.setDetailedPermissions(permissionData.detailedPermissions);
    }
  }, [dispatch, permissionData, selectedExtranet]);

  // Clear stored permissions only when extranet selection actually changes
  useEffect(() => {
    const previousExtranet = localStorage.getItem('previous_extranet');
    if (selectedExtranet && selectedExtranet !== previousExtranet) {
      StorageService.clearModulePermissions();
      StorageService.clearDetailedPermissions();
      localStorage.setItem('previous_extranet', selectedExtranet);
    }
  }, [selectedExtranet]);
  

  const token = StorageService.getAccessToken();
  if (token && StorageService.isTokenExpired()) {
    StorageService.clearTokens();
    return <Navigate to="/login" replace />;
  }
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const forceTempPassword = StorageService.getForceTempPassword();
  if (forceTempPassword === 'true') {
    return <Navigate to="/change-temp-password" replace />;
  }

  if (requiredPermission && !storedModulePermissions?.[requiredPermission]) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check detailed permission if specified
  if (requiredDetailedPermission) {
    const [module, permission] = requiredDetailedPermission.split('.');
    const hasDetailedPermission = storedDetailedPermissions?.[module]?.[permission];
    if (!hasDetailedPermission) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
}

export default RouteInternal;
